import { Button } from '@creditornot/cb-button';
import { useMutation, useQuery } from '@tanstack/react-query';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { APIErrorNotification } from 'common/components';
import GirlShrugging from 'common/illustrations/GirlShrugging';
import { Typography as T } from 'common/typography';
import { getSelectionBootstrappingStatus, resetSelectionBootstrapping } from 'modules/shared-menu/api';
import { useScopeContext } from 'modules/shared-menu/contexts/ScopeContext';
import { useSharedMenuData } from 'modules/shared-menu/hooks/useSharedMenuData';

import { useSelectionData } from '../../../../hooks/useSelectionData';

const Root = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  margin-block-end: 12px;
`;

const StyledGirlShrugging = styled(GirlShrugging)`
  width: 100%;
  max-width: 375px;
  margin-block-end: 16px;
`;

export const BootstrapFailure = () => {
  const scope = useScopeContext();
  const { updateSharedMenuStatus } = useSharedMenuData({ scope });
  const { selection, refetchSelectionData } = useSelectionData();

  const { data: bootstrapStatus } = useQuery(
    ['selection-bootstrapping-status', selection.id],
    () => getSelectionBootstrappingStatus(selection.id),
    {
      // Only use the separate endpoint if response doesn't already contain the error message
      enabled: !selection.draft?.bootstrapping?.error_message,
    },
  );

  const { mutate, isLoading } = useMutation(
    ['reset-bootstrap', selection.id],
    () => resetSelectionBootstrapping(selection.id),
    {
      onSuccess: async () => {
        updateSharedMenuStatus(selection.id, undefined);
        await refetchSelectionData();
      },
    },
  );

  const errorMessageToShow = selection.draft?.bootstrapping?.error_message || bootstrapStatus?.error_message;

  return (
    <Root>
      <StyledGirlShrugging />
      <HeaderContainer>
        <T.Heading6>
          <FormattedMessage id="shared-menu.editor.empty-menu.bootstrap.failure.header" />
        </T.Heading6>
      </HeaderContainer>
      {errorMessageToShow && <APIErrorNotification error={errorMessageToShow} style={{ margin: '12px 24px' }} />}
      <T.Body3>
        <FormattedMessage id="listing-manager.selections.editor.bootstrap.failure.subheader" />
      </T.Body3>
      <Button
        variant="secondary"
        size="small"
        onClick={() => mutate()}
        loading={isLoading}
        style={{ marginBlockStart: '16px' }}
      >
        <FormattedMessage id="listing-manager.selections.editor.bootstrap.reset-bootstrap" />
      </Button>
    </Root>
  );
};
